import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import React, { ReactNode } from 'react';

import { BoxColumn } from '@components/Box';

export interface CollapsibleBoxColumnProps {
  children: ReactNode;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  canCollapse?: boolean;
  collapseTitle?: string;
  disabledTitle?: string;
  className?: string;
}

/**
 * CollapsibleBoxColumn Component
 *
 * A reusable component that wraps content in a BoxColumn with collapsible functionality.
 * Features a toggle button that allows users to collapse/expand the column.
 *
 * @param children - The content to display inside the collapsible column
 * @param isCollapsed - Whether the column is currently collapsed
 * @param onToggleCollapse - Callback function when toggle button is clicked
 * @param canCollapse - Whether the collapse functionality is enabled (optional, defaults to true)
 * @param collapseTitle - Tooltip text when collapse is enabled (optional)
 * @param disabledTitle - Tooltip text when collapse is disabled (optional)
 * @param className - Additional CSS classes for the container div (optional)
 */
export const CollapsibleBoxColumn: React.FC<CollapsibleBoxColumnProps> = ({
  children,
  isCollapsed,
  onToggleCollapse,
  canCollapse = true,
  collapseTitle = 'Collapse form',
  disabledTitle = 'Please select a customer first',
  className = 'w-full lg:w-[65%] md:w-full transition-all duration-300',
}) => {
  if (isCollapsed) {
    return null;
  }

  return (
    <div className={className}>
      <div className="relative">
        {/* Toggle Button */}
        <button
          type="button"
          onClick={() => {
            if (canCollapse) {
              onToggleCollapse();
            }
          }}
          disabled={!canCollapse}
          title={canCollapse ? collapseTitle : disabledTitle}
          className={`hidden lg:block absolute right-3 -top-2 z-10 rounded-full p-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
            canCollapse
              ? 'bg-white border border-gray-300 hover:bg-gray-50 cursor-pointer'
              : 'bg-gray-100 border border-gray-200 cursor-not-allowed opacity-50'
          }`}
        >
          <ChevronLeftIcon
            className={`h-4 w-4 ${
              canCollapse ? 'text-gray-600' : 'text-gray-400'
            }`}
          />
        </button>

        <BoxColumn>{children}</BoxColumn>
      </div>
    </div>
  );
};
